import React, { useState, useEffect, useRef } from 'react';
import { styled } from 'styletron-react';
import { Phone, CreditCard, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import QRCode from 'react-qr-code';
import { toBlob } from 'html-to-image';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { WalletPassLoader } from '../../components/WalletPassLoader';
import { InvitationLoader } from '../../components/InvitationLoader';
import { GuestScanForm, type GuestInfo } from '../../components/GuestScanForm';
import { effectiveCodeChannel, isWhatsappUnavailable, whatsappCodeEnabled } from '../../utils/codeChannel';
import { analyzeDocument, acceptInvitation, acceptBusinessAccess, getPublicEvent, sendSmsCode, verifySmsCode, clearSmsToken, type CodeDeliveryMethod } from '../../api/publicApi';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { useLang } from '../../i18n/publicEvents';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import { usePhoneCountry, sanitizeNationalNumber, CountryCodeButton, CountryPickerModal } from './PhoneCountryPicker';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Convert a File to base64 (without the data: prefix)
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:image/jpeg;base64,XXXX"; strip prefix
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

// --- Styled (matches Flutter app layout) ---

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px 16px',
  boxSizing: 'border-box',
});

const Container = styled('div', {
  width: '100%',
  maxWidth: '440px',
  padding: '28px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  boxSizing: 'border-box',
});

const Logo = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  padding: '24px 0',
});

const Title = styled('h1', {
  fontSize: '28px',
  fontWeight: 600,
  color: '#000',
  textAlign: 'center',
  lineHeight: 1.3,
  margin: '24px 0 16px',
});

const Subtitle = styled('p', {
  fontSize: '15px',
  color: '#333',
  lineHeight: 1.5,
  margin: '0 0 20px',
});

const StepRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '14px 0',
  borderBottom: '1px solid #E5E7EB',
});

const StepIcon = styled('div', {
  color: '#000',
  flexShrink: 0,
});

const StepText = styled('span', {
  fontSize: '16px',
  color: '#000',
  flex: 1,
});

const PrimaryButton = styled('button', {
  width: '100%',
  padding: '16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #000)',
  color: '#fff',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: '24px',
  transition: 'opacity 0.15s',
  ':hover': { opacity: 0.9 },
});

const QRContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  flex: 1,
});

const WalletButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 24px',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  marginTop: '16px',
});

const SaveImageButton = styled('button', {
  width: '100%',
  maxWidth: '380px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '14px 24px',
  borderRadius: '10px',
  border: '1px solid var(--tenant-primary, #000)',
  backgroundColor: 'var(--tenant-primary, #000)',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  marginTop: '24px',
  transition: 'opacity 0.15s',
  ':hover': { opacity: 0.9 },
});

const LoaderContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '12px',
  color: '#666',
});

const Spinner = styled('div', {
  width: '32px',
  height: '32px',
  border: '3px solid #E5E7EB',
  borderTopColor: 'var(--tenant-primary, #000)',
  borderRadius: '50%',
  animationName: [{
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  }] as any,
  animationDuration: '0.6s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear',
});

const ErrorContainer = styled('div', {
  textAlign: 'center',
  padding: '80px 24px',
  color: '#666',
});

// --- Registration flow ---

const TopBar = styled('div', {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const BackBtn = styled('button', {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  color: '#000',
});

const TopBarTitle = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  color: '#000',
});

const ScanBox = styled('label', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '40px 16px',
  border: '2px dashed #D1D5DB',
  borderRadius: '12px',
  cursor: 'pointer',
  textAlign: 'center',
  margin: '20px 0',
  color: '#6B7280',
  ':hover': { borderColor: 'var(--tenant-primary, #000)', color: 'var(--tenant-primary, #000)' },
});

const ScanPreview = styled('img', {
  width: '120px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
});

const FieldLabel = styled('label', {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
});

const TextInput = styled('input', {
  width: '100%',
  padding: '12px 14px',
  // 16px minimum to stop iOS Safari from auto-zooming on focus.
  fontSize: '16px',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  ':focus': { borderColor: 'var(--tenant-primary, #000)' },
});

const ErrorText = styled('div', {
  color: '#DC2626',
  fontSize: '13px',
  marginTop: '8px',
  textAlign: 'center',
});

const GuestRow = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '14px',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  marginBottom: '12px',
});

const SmallLabel = styled('div', {
  fontSize: '11px',
  fontWeight: 600,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '4px',
});

// --- Pass card (mini pkpass preview) ---

const PassCard = styled('div', {
  width: '100%',
  maxWidth: '380px',
  backgroundColor: '#fff',
  borderRadius: '18px',
  boxShadow: '0 12px 36px -8px rgba(0,0,0,0.18), 0 4px 12px -4px rgba(0,0,0,0.08)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
});

const PassHeader = styled('div', {
  position: 'relative',
  background: `
    radial-gradient(circle at 100% 0%, rgba(255,255,255,0.18), transparent 50%),
    radial-gradient(circle at 0% 100%, rgba(0,0,0,0.20), transparent 60%),
    linear-gradient(135deg, var(--tenant-primary, #000) 0%, var(--tenant-primary-dark, var(--tenant-primary, #000)) 100%)
  `,
  padding: '22px 24px 26px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  color: '#fff',
  borderRadius: '18px 18px 0 0',
  overflow: 'hidden',
  '::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 1.5px)',
    backgroundSize: '14px 14px',
    pointerEvents: 'none',
  } as any,
});

const PassLogoBox = styled('div', {
  width: '60px',
  height: '60px',
  borderRadius: '14px',
  background: 'rgba(255,255,255,0.92)',
  border: '1px solid rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(4px)',
  position: 'relative',
  zIndex: 1,
  overflow: 'hidden',
});

const PassHeaderTitle = styled('div', {
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
});

const PassHeaderSubtitle = styled('div', {
  fontSize: '10px',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  opacity: 0.75,
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
});

const Perforation = styled('div', {
  position: 'relative',
  height: '22px',
  background: '#fff',
  display: 'flex',
  alignItems: 'center',
  '::before': {
    content: '""',
    position: 'absolute',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#FFFFFF',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
    top: '50%',
    left: '-11px',
    transform: 'translateY(-50%)',
  } as any,
  '::after': {
    content: '""',
    position: 'absolute',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#FFFFFF',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
    top: '50%',
    right: '-11px',
    transform: 'translateY(-50%)',
  } as any,
});

const PerforationLine = styled('div', {
  flex: 1,
  height: 0,
  borderTop: '2px dashed #D1D5DB',
  margin: '0 14px',
});

const PassQRWrap = styled('div', {
  backgroundColor: '#fff',
  padding: '20px 20px 14px',
  display: 'flex',
  justifyContent: 'center',
});

const PassQRFrame = styled('div', {
  background: '#fff',
  borderRadius: '12px',
  padding: '8px',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
});

const PassFields = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px 16px',
  padding: '4px 24px 18px',
});

const PassFieldLabel = styled('div', {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#6B7280',
  marginBottom: '4px',
});

const PassFieldValue = styled('div', {
  fontSize: '15px',
  fontWeight: 600,
  color: '#111827',
  wordBreak: 'break-word',
  lineHeight: 1.3,
});

const PinBadge = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  background: '#F3F4F6',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  fontFamily: '"SF Mono", Menlo, Consolas, monospace',
  fontSize: '15px',
  fontWeight: 600,
  color: '#111827',
  letterSpacing: '0.08em',
});

const PassFooter = styled('div', {
  borderTop: '1px solid #F3F4F6',
  padding: '14px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  fontSize: '12px',
  color: '#6B7280',
  letterSpacing: '0.02em',
  borderRadius: '0 0 18px 18px',
});

// --- Helpers ---

const getStatusConfirmed = (data: any): boolean => {
  const statusId = data?.status_id || data?.business_access_status_id || 1;
  return statusId === 2 || statusId === 3 || statusId === 4;
};

/** SAM S-38: a cancelled invitation (status 6) gets no form, SMS, PIN, QR or pass. */
export const getStatusCancelled = (data: any): boolean =>
  Number(data?.status_id || data?.business_access_status_id) === 6;

const formatPassDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPassTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// --- Component ---

interface PublicInvitationProps {
  type: 'invitation' | 'access' | 'event';
  id: string;
}

type GuestState = GuestInfo & { userId: number; photo_url: string };
const emptyGuestForm: GuestInfo = {
  name: '', last_name: '', document_id: '', dob: '', nationality: '', expiration_date: '',
};

export const PublicInvitation: React.FC<PublicInvitationProps> = ({ type, id }) => {
  usePublicPageScroll();
  const { t } = useLang();
  const { tenantLogo, tenantName, tenantPrimaryColor, tenantApiKey, tenantLoading, whatsappCode } = useTenantBranding();
  const { imageUrl: tenantLogoUrl } = useS3Image(tenantLogo);
  const [data, setData] = useState<any>(null);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  // CORS-safe copy of the tenant logo for the "save as image" capture. The logo
  // lives in an S3 bucket that doesn't send CORS headers, so html-to-image can't
  // read its pixels off the live <img>. We try to bake it into a data URL via a
  // crossOrigin image; if the bucket ever lacks CORS this stays empty and the
  // on-screen <img> still renders fine (display doesn't require CORS).
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const passCardRef = useRef<HTMLDivElement>(null);

  // Registration flow state — declared at top to satisfy Rules of Hooks (no early returns above)
  const [flowStep, setFlowStep] = useState<'welcome' | 'phone' | 'pin' | 'guestForm' | 'submitting'>('welcome');
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  const [guests, setGuests] = useState<GuestState[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [scannedPreview, setScannedPreview] = useState('');
  const [formError, setFormError] = useState<string>('');
  // Phone + PIN verification (mirrors the Flutter accept-invitation flow)
  // National number only; the E.164 value is `phoneCountry.buildFullPhone(phoneNumber)`.
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const phoneCountry = usePhoneCountry({ apiKey: tenantApiKey, enabled: !tenantLoading });
  const fullPhone = phoneCountry.buildFullPhone(phoneNumber);
  const [deliveryMethod, setDeliveryMethod] = useState<CodeDeliveryMethod>('SMS');
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [smsError, setSmsError] = useState<string>('');
  const [smsBusy, setSmsBusy] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  // Cloudflare Turnstile token. Single-use — bumped via captchaKey to force a
  // fresh widget after each /sms/v2 call (success or failure).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const apiKey = tenantApiKey;

  useEffect(() => {
    if (tenantLoading) return;
    if (!apiKey) {
      setError('Tenant not configured');
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);
    const startedAt = Date.now();
    const minDuration = 3500;

    const fetchData = async () => {
      try {
        let url = '';
        if (type === 'invitation') {
          url = `${API_BASE_URL}/invitation?code=${id}`;
        } else if (type === 'access') {
          url = `${API_BASE_URL}/business_access/${id}`;
        } else {
          url = `${API_BASE_URL}/crud/event/${id}`;
        }

        const resp = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': apiKey,
          },
        });

        if (!resp.ok) throw new Error('Not found');

        const json = await resp.json();
        const record = json?.data?.[0] || json?.data || json;
        if (!record || (Array.isArray(record) && record.length === 0)) {
          throw new Error('Invitation not found');
        }
        setData(Array.isArray(record) ? record[0] : record);
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally {
        const elapsed = Date.now() - startedAt;
        if (elapsed < minDuration) {
          await new Promise(res => setTimeout(res, minDuration - elapsed));
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [type, id, apiKey, tenantLoading]);

  // Pre-fill the phone field with the main_guest's tel when the invitation/access loads.
  // A stored E.164 value is split into country code + national number using the
  // loaded country list, so the user never ends up with a doubled prefix.
  const phonePrefilledRef = useRef(false);
  useEffect(() => {
    const tel = data?.main_guest?.tel;
    if (!tel || phonePrefilledRef.current || phoneNumber) return;
    if (String(tel).trim().startsWith('+') && !phoneCountry.countriesReady) return;
    phonePrefilledRef.current = true;
    setPhoneNumber(phoneCountry.applyStoredPhone(String(tel)));
    // intentionally only react to the API value + country list; user edits are kept untouched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.main_guest?.tel, phoneCountry.countriesReady]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = window.setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendTimer]);

  // Drop the SMS-verified token on unmount so it doesn't linger past the
  // user's session — token also auto-expires in 5 min server-side.
  useEffect(() => {
    return () => clearSmsToken();
  }, []);

  // When the access record points to an event, fetch the event so we can show
  // its name in the pass card and forward it to the wallet pkpass generator.
  useEffect(() => {
    const eventId = data?.event_id;
    if (!eventId || !apiKey) {
      setEvent(null);
      return;
    }
    let cancelled = false;
    getPublicEvent(String(eventId), apiKey)
      .then(ev => { if (!cancelled) setEvent(ev || null); })
      .catch(() => { if (!cancelled) setEvent(null); });
    return () => { cancelled = true; };
  }, [data?.event_id, apiKey]);

  // Pre-bake the tenant logo into a CORS-safe data URL for image capture.
  useEffect(() => {
    if (!tenantLogoUrl) { setLogoDataUrl(''); return; }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const url = canvas.toDataURL('image/png');
        if (!cancelled) setLogoDataUrl(url);
      } catch {
        // Tainted canvas (bucket has no CORS) — leave empty; capture will skip the logo.
        if (!cancelled) setLogoDataUrl('');
      }
    };
    img.onerror = () => { if (!cancelled) setLogoDataUrl(''); };
    img.src = tenantLogoUrl;
    return () => { cancelled = true; };
  }, [tenantLogoUrl]);

  if (loading) {
    return (
      <LoaderContainer>
        <InvitationLoader color={tenantPrimaryColor} />
      </LoaderContainer>
    );
  }

  if (error || !data) {
    return (
      <Page>
        <ErrorContainer>
          <h2 style={{ color: '#000', marginBottom: 8 }}>Invitation not found</h2>
          <p>An error occurred loading the invitation details.</p>
        </ErrorContainer>
      </Page>
    );
  }

  if (getStatusCancelled(data)) {
    return (
      <Page>
        <ErrorContainer>
          <h2 role="alert" style={{ color: '#000', marginBottom: 8 }}>{t('invitationCancelled')}</h2>
          <p>{t('invitationCancelledHint')}</p>
        </ErrorContainer>
      </Page>
    );
  }

  const mainGuest = data.main_guest || {};
  const guestName = [mainGuest.name, mainGuest.last_name].filter(Boolean).join(' ') || 'Guest';
  const fullEstateName = [data.estate_group_name, data.estate_name].filter(Boolean).join(' ').trim();
  const hostName = data.host_name || fullEstateName || data.business_name || '';
  const accessCode = data.code || data.legacy_code || id;
  const isConfirmed = getStatusConfirmed(data);
  const gateExternal = data.gate_rules?.external_rule || data.gate_rules?.external || '';
  const hikPin = data.hik_pin || '';
  const passFromIso = data.start_date || data.invitation_from || '';
  const passToIso = data.end_date || data.invitation_to || '';

  // Party size — invitations expose `companions[]`, business_access exposes
  // `business_guests[]` (JSONB array). Children come as a number on both shapes.
  const businessGuestsArr: any[] = Array.isArray(data.business_guests) ? data.business_guests : [];
  const companionsCount = (Array.isArray(data.companions) ? data.companions.length : 0) + businessGuestsArr.length;
  const childrenCount = Number(data.children_count || 0);
  const partyTotal = 1 + companionsCount + childrenCount;

  // Event linkage — when the access is tied to an event, surface its name in
  // the header subtitle (below the business) and forward it to the pkpass.
  const eventName: string = (event?.name_en || event?.name_es || event?.name || '').toString().trim();

  const destinationLabel = (() => {
    if (type === 'invitation') return fullEstateName || data.estate_name || data.host_name || tenantName;
    if (type === 'event') return data.name || data.event_name || tenantName;
    // access: backend enriches the record with `business_name` (see business_access GET handler)
    return data.business_name || hostName || tenantName;
  })();

  // --- Registration flow derived values (state lives at top) ---
  const companions: Array<any> = Array.isArray(data.companions) ? data.companions : [];
  const totalGuests = 1 + companions.length;
  const isMainGuest = currentGuestIndex === 0;
  const isLastGuest = currentGuestIndex >= totalGuests - 1;
  const currentGuest = guests[currentGuestIndex] || { ...emptyGuestForm, userId: 0, photo_url: '' };
  const isFormValid = !!(currentGuest.name && currentGuest.last_name && currentGuest.document_id);

  const startFlow = () => {
    const initial: GuestState[] = [
      { ...emptyGuestForm, userId: mainGuest.id || 0, photo_url: '' },
      ...companions.map((c: any) => ({ ...emptyGuestForm, userId: c.id || 0, photo_url: '' })),
    ];
    setGuests(initial);
    setCurrentGuestIndex(0);
    setScannedPreview('');
    setFormError('');
    setFlowStep('guestForm');
  };

  // SAM-922: with Turnstile on, the token arrives a moment after the widget mounts (interaction-only), and again
  // after it expires. Until it is there the send buttons wait and say so, instead of failing on the first click.
  const captchaPending = !!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken;

  const handleSendSmsCode = async () => {
    const tel = fullPhone;
    if (!phoneNumber.replace(/\D/g, '')) { setSmsError('Phone number is required'); return; }
    // Captcha is required when the site key is configured. When it's not,
    // the widget renders nothing and the backend also fails open, so dev
    // keeps working.
    const turnstileEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (turnstileEnabled && !captchaToken) {
      setSmsError('Please complete the verification challenge');
      return;
    }
    setSmsBusy(true); setSmsError('');
    try {
      await sendSmsCode(tel, apiKey, captchaToken || undefined, effectiveCodeChannel(deliveryMethod, whatsappCode));
      setResendTimer(30);
      setPinDigits(['', '', '', '']);
      setFlowStep('pin');
    } catch (e: any) {
      if (isWhatsappUnavailable(e)) {
        // The tenant lost its WhatsApp sender: stay on the phone step with SMS chosen.
        setDeliveryMethod('SMS');
        setSmsError(t('whatsappUnavailable'));
      } else {
        setSmsError(e?.response?.data?.message || 'Failed to send code');
      }
    } finally {
      // Turnstile tokens are single-use — discard ours and force a fresh
      // widget so the next attempt has a valid token.
      setCaptchaToken(null);
      setCaptchaKey(k => k + 1);
      setSmsBusy(false);
    }
  };

  const handleVerifyPin = async () => {
    const code = pinDigits.join('');
    if (code.length < 4) { setSmsError('Enter the full 4-digit code'); return; }
    setSmsBusy(true); setSmsError('');
    try {
      const res: any = await verifySmsCode(fullPhone, code, apiKey);
      if (res?.statusCode === 400) { setSmsError(res.message || 'Invalid code'); return; }
      // Verified — kick off the guest scan flow
      startFlow();
    } catch (e: any) {
      setSmsError(e?.response?.data?.message || 'Invalid verification code');
    } finally {
      setSmsBusy(false);
    }
  };

  const handlePinDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    setPinDigits(prev => prev.map((d, i) => i === index ? digit : d));
    if (digit && index < 3) {
      const next = document.getElementById(`pin-${index + 1}`);
      next?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      const prev = document.getElementById(`pin-${index - 1}`);
      prev?.focus();
    }
  };

  const handleScanDocument = async (file: File) => {
    setScanning(true);
    setScanStatus('loading');
    setFormError('');
    setScannedPreview('');
    let didFail = false;
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1];
      setScannedPreview(dataUrl);
      const ocr = await analyzeDocument(base64, file.name, apiKey);
      setGuests(gs => gs.map((g, i) => i === currentGuestIndex ? {
        ...g,
        document_id: ocr.number || g.document_id,
        name: ocr.name || g.name,
        last_name: ocr.LastName || g.last_name,
        dob: ocr.DOB || g.dob,
        nationality: ocr.nationality || g.nationality,
        expiration_date: ocr.expDate || g.expiration_date,
        photo_url: ocr.image || ocr.picture || g.photo_url,
      } : g));
    } catch {
      setFormError('Could not analyze the document. Please fill in the information manually.');
      didFail = true;
    } finally {
      setScanStatus(didFail ? 'error' : 'success');
      window.setTimeout(() => setScanning(false), 1400);
    }
  };

  const handleGuestFieldChange = (field: keyof GuestInfo, value: string) => {
    setGuests(gs => gs.map((g, i) => i === currentGuestIndex ? { ...g, [field]: value } : g));
  };

  const handleGuestBack = () => {
    if (currentGuestIndex > 0) {
      setCurrentGuestIndex(i => i - 1);
      setScannedPreview('');
    } else {
      setFlowStep('welcome');
    }
  };

  const handleGuestContinue = async () => {
    if (!isFormValid) {
      setFormError('Please fill in identification number, name, and last name');
      return;
    }
    setFormError('');
    if (isLastGuest) {
      await handleSubmitInvitation();
    } else {
      setCurrentGuestIndex(i => i + 1);
      setScannedPreview('');
    }
  };

  const handleSubmitInvitation = async () => {
    setFlowStep('submitting');
    setFormError('');
    try {
      const [main, ...rest] = guests;
      const mainGuestPayload = main
        ? {
            name: main.name.trim(),
            last_name: main.last_name.trim(),
            photo_url: main.photo_url,
            idnum: main.document_id?.trim() || undefined,
          }
        : undefined;
      const companionsPayload = rest.map((g) => ({
        user_id: g.userId,
        name: g.name.trim(),
        last_name: g.last_name.trim(),
        photo_url: g.photo_url,
        idnum: g.document_id?.trim() || undefined,
      }));

      if (type === 'access') {
        await acceptBusinessAccess(
          String(data.id),
          {
            business_access_status_id: 2,
            main_guest: mainGuestPayload,
            companions: companionsPayload,
          },
          apiKey,
        );
      } else {
        // 'invitation' (and any other future invitation-shaped flow) goes to invitation lambda
        await acceptInvitation(
          data.id,
          {
            status_id: 2,
            main_guest: mainGuestPayload,
            companions: companionsPayload,
          },
          apiKey,
        );
      }
      // Reload to refetch with confirmed status + hik_pin → pass card renders
      window.location.reload();
    } catch (e: any) {
      setFormError(e?.message || 'Failed to confirm invitation');
      setFlowStep('guestForm');
    }
  };

  // --- CONFIRMED VIEW: Mini pass card + Wallet ---
  if (isConfirmed) {
    const startDate = formatPassDate(passFromIso);
    const startTime = formatPassTime(passFromIso);
    const endDate = formatPassDate(passToIso);
    const endTime = formatPassTime(passToIso);
    const sameDay = startDate && startDate === endDate;

    // Capture the pass card as a PNG and let the user save it to their gallery.
    // On mobile we use the Web Share API (offers "Save Image"); elsewhere we
    // fall back to a normal file download. The QR is an inline SVG and the
    // logo is inlined by html-to-image, so the canvas is never tainted.
    const handleSaveImage = async () => {
      const node = passCardRef.current;
      if (!node || savingImage) return;
      setSavingImage(true);
      try {
        const blob = await toBlob(node, {
          pixelRatio: 3,
          // NOTE: do NOT enable cacheBust — it appends a `?<timestamp>` query
          // param, which breaks the tenant logo's S3 presigned-URL signature
          // (403 → the error body gets embedded as the image → black box).
          backgroundColor: '#FFFFFF',
          // If the tenant logo can't be inlined (bucket without CORS headers),
          // fall back to a fully TRANSPARENT pixel so the logo chip stays clean
          // (a non-transparent placeholder shows up as a black box).
          imagePlaceholder:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
        });
        if (!blob) throw new Error('Could not render pass image');

        const fileName = `pase-${(guestName || 'acceso').replace(/\s+/g, '-').toLowerCase()}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });

        const nav = navigator as Navigator & { canShare?: (data?: any) => boolean };
        if (nav.share && nav.canShare?.({ files: [file] })) {
          try {
            await nav.share({ files: [file], title: tenantName || 'Access Pass' });
            return;
          } catch (shareErr: any) {
            // User cancelled the share sheet — do not fall through to download.
            if (shareErr?.name === 'AbortError') return;
          }
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Save pass image error:', err);
      } finally {
        setSavingImage(false);
      }
    };

    return (
      <Page>
        <QRContainer>
          <PassCard ref={passCardRef}>
            <PassHeader>
              {tenantLogoUrl ? (
                <PassLogoBox>
                  <img
                    src={logoDataUrl || tenantLogoUrl}
                    alt={tenantName}
                    style={{ maxHeight: '40px', maxWidth: '48px', objectFit: 'contain' }}
                  />
                </PassLogoBox>
              ) : (
                <PassLogoBox>
                  <span style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                    {(tenantName || hostName || 'A').charAt(0).toUpperCase()}
                  </span>
                </PassLogoBox>
              )}
              <PassHeaderTitle>{tenantName || hostName || 'Access Pass'}</PassHeaderTitle>
              {destinationLabel && destinationLabel !== tenantName && <PassHeaderSubtitle>{destinationLabel}</PassHeaderSubtitle>}
              {eventName && <PassHeaderSubtitle style={{ opacity: 0.92, fontWeight: 600, letterSpacing: '0.16em' }}>{eventName}</PassHeaderSubtitle>}
            </PassHeader>

            <Perforation>
              <PerforationLine />
            </Perforation>

            <PassQRWrap>
              <PassQRFrame>
                <QRCode
                  value={accessCode}
                  size={200}
                  level="H"
                  style={{ width: '200px', height: '200px', display: 'block' }}
                />
              </PassQRFrame>
            </PassQRWrap>

            <PassFields>
              <div style={{ gridColumn: '1 / -1' }}>
                <PassFieldLabel>Guest</PassFieldLabel>
                <PassFieldValue>{guestName}</PassFieldValue>
              </div>

              {sameDay ? (
                <>
                  <div>
                    <PassFieldLabel>Date</PassFieldLabel>
                    <PassFieldValue>{startDate}</PassFieldValue>
                  </div>
                  <div>
                    <PassFieldLabel>Time</PassFieldLabel>
                    <PassFieldValue>{startTime}{endTime && startTime !== endTime ? ` – ${endTime}` : ''}</PassFieldValue>
                  </div>
                </>
              ) : (
                <>
                  {startDate && (
                    <div>
                      <PassFieldLabel>From</PassFieldLabel>
                      <PassFieldValue>{startDate}</PassFieldValue>
                    </div>
                  )}
                  {endDate && (
                    <div>
                      <PassFieldLabel>To</PassFieldLabel>
                      <PassFieldValue>{endDate}</PassFieldValue>
                    </div>
                  )}
                </>
              )}

              {(companionsCount > 0 || childrenCount > 0) && (
                <>
                  <div>
                    <PassFieldLabel>Party</PassFieldLabel>
                    <PassFieldValue>{partyTotal} {partyTotal === 1 ? 'person' : 'people'}</PassFieldValue>
                  </div>
                  <div>
                    <PassFieldLabel>Breakdown</PassFieldLabel>
                    <PassFieldValue style={{ fontSize: 13, fontWeight: 500 }}>
                      {[
                        '1 main',
                        companionsCount > 0 && `${companionsCount} companion${companionsCount === 1 ? '' : 's'}`,
                        childrenCount > 0 && `${childrenCount} ${childrenCount === 1 ? 'child' : 'children'}`,
                      ].filter(Boolean).join(' • ')}
                    </PassFieldValue>
                  </div>
                </>
              )}

              {hikPin && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <PassFieldLabel>PIN</PassFieldLabel>
                  <PinBadge>{hikPin}</PinBadge>
                </div>
              )}

              {gateExternal && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <PassFieldLabel>{gateExternal.includes(',') ? 'Gates' : 'Gate'}</PassFieldLabel>
                  <PassFieldValue>{gateExternal}</PassFieldValue>
                </div>
              )}
            </PassFields>

            <PassFooter>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22 s-7 -7 -7 -13 a7 7 0 0 1 14 0 c0 6 -7 13 -7 13 z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
              Present this QR code at the entrance
            </PassFooter>
          </PassCard>

          <SaveImageButton
            disabled={savingImage}
            style={{ opacity: savingImage ? 0.6 : 1, cursor: savingImage ? 'default' : 'pointer' }}
            onClick={handleSaveImage}
          >
            <Download size={18} />
            {savingImage ? t('generatingPass') : t('savePassToGallery')}
          </SaveImageButton>

          <WalletButton
            disabled={walletLoading}
            style={{ opacity: walletLoading ? 0.5 : 1 }}
            onClick={async () => {
              setWalletLoading(true);
              const startedAt = Date.now();
              const minDuration = 1800;
              let resultUrl: string | null = null;
              try {
                const resp = await fetch(`${API_BASE_URL}/wallet`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': apiKey,
                  },
                  body: JSON.stringify({
                    qrLink: accessCode,
                    code: accessCode,
                    type: 'access',
                    companyName: hostName || tenantName,
                    from: passFromIso,
                    to: passToIso,
                    gate: gateExternal || '',
                    destination: hostName || tenantName || '',
                    tenant_id: data.tenant_id || '',
                    hikPin: hikPin || undefined,
                    eventName: eventName || undefined,
                  }),
                });
                if (resp.ok) {
                  const result = await resp.json();
                  resultUrl = result.url || null;
                }
              } catch (err) {
                console.error('Wallet pass error:', err);
              }
              const elapsed = Date.now() - startedAt;
              if (elapsed < minDuration) {
                await new Promise(res => setTimeout(res, minDuration - elapsed));
              }
              setWalletLoading(false);
              if (resultUrl) window.open(resultUrl, '_blank');
            }}
          >
            <img src="/apple_wallet.png" alt="Add to Apple Wallet" style={{ height: '50px', objectFit: 'contain' }} />
          </WalletButton>
        </QRContainer>

        {walletLoading && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(3px)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <WalletPassLoader
              status="loading"
              size="md"
              color={tenantPrimaryColor}
              background="#fff"
            />
          </div>
        )}
      </Page>
    );
  }

  const renderLogo = () => (
    <Logo>
      {tenantLogoUrl ? (
        <img src={tenantLogoUrl} alt={tenantName} style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain' }} />
      ) : tenantName ? (
        <div style={{ fontSize: '28px', fontWeight: 600, color: tenantPrimaryColor }}>{tenantName}</div>
      ) : null}
    </Logo>
  );

  // --- NOT CONFIRMED VIEW ---
  if (flowStep === 'welcome') {
    return (
      <Page>
        <Container>
          {renderLogo()}
          <Title>
            Hello {guestName},{' '}
            {hostName ? `you have been invited by ${hostName}` : 'you have been invited'}
          </Title>
          {/* SAM-829: the door PIN is already issued while the invitation is pending (backend #644): show it. */}
          {hikPin && (
            <div data-testid="pending-pin" style={{ textAlign: 'center', marginBottom: 16 }}>
              <PassFieldLabel>PIN</PassFieldLabel>
              <PinBadge>{hikPin}</PinBadge>
            </div>
          )}
          <Subtitle>
            In order to complete your invitation please follow these steps:
          </Subtitle>
          <StepRow>
            <StepIcon><Phone size={22} /></StepIcon>
            <StepText>Confirm phone number</StepText>
          </StepRow>
          <StepRow>
            <StepIcon><CreditCard size={22} /></StepIcon>
            <StepText>
              Confirm {totalGuests > 1 ? `${totalGuests} ID documents` : 'your ID document'}
            </StepText>
          </StepRow>
          <PrimaryButton onClick={() => { setSmsError(''); setFlowStep('phone'); }}>
            Online Registration
            <ArrowRight size={18} style={{ marginLeft: '8px', verticalAlign: 'middle' }} />
          </PrimaryButton>
        </Container>
      </Page>
    );
  }

  if (flowStep === 'phone') {
    return (
      <Page>
        <Container>
          <TopBar>
            <BackBtn onClick={() => { setSmsError(''); setFlowStep('welcome'); }}>
              <ArrowLeft size={22} />
            </BackBtn>
            <TopBarTitle>Verify phone</TopBarTitle>
            <div style={{ width: '34px' }} />
          </TopBar>

          {renderLogo()}

          <Title style={{ fontSize: '22px' }}>Confirm your phone number</Title>
          <Subtitle style={{ textAlign: 'center' }}>
            We'll send a 4-digit code to this number to verify it's you.
          </Subtitle>

          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <CountryCodeButton
              countryCode={phoneCountry.countryCode}
              flag={phoneCountry.countryFlag}
              onClick={() => phoneCountry.setShowPicker(true)}
            />
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(sanitizeNationalNumber(e.target.value))}
              placeholder="809 555 0100"
              style={{
                flex: 1,
                minWidth: 0,
                padding: '12px 14px',
                fontSize: '16px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {phoneCountry.showPicker && (
            <CountryPickerModal
              countries={phoneCountry.filteredCountries}
              search={phoneCountry.search}
              onSearchChange={phoneCountry.setSearch}
              onSelect={phoneCountry.selectCountry}
              onClose={() => { phoneCountry.setShowPicker(false); phoneCountry.setSearch(''); }}
              searchPlaceholder={t('searchCountry')}
            />
          )}

          {/* Channel selector: only when WhatsApp codes are on (SAM-1065); otherwise the code goes by SMS. */}
          {whatsappCodeEnabled(whatsappCode) && (
          <div style={{ marginTop: '14px' }}>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>
              How would you like to receive the code?
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {([
                { id: 'SMS' as const, label: 'SMS' },
                { id: 'WHATSAPP' as const, label: 'WhatsApp' },
              ]).map(({ id, label }) => {
                const selected = deliveryMethod === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDeliveryMethod(id)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      fontSize: '15px',
                      fontWeight: selected ? 600 : 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      border: selected ? '2px solid var(--tenant-primary, #000)' : '1px solid #D1D5DB',
                      backgroundColor: selected ? 'var(--tenant-primary, #000)' : '#FFFFFF',
                      color: selected ? '#FFFFFF' : '#374151',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {smsError && (
            <div style={{ color: '#DC2626', fontSize: '13px', textAlign: 'center', marginTop: '8px' }}>
              {smsError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
            <TurnstileWidget key={captchaKey} onToken={setCaptchaToken} action="send-sms-code" />
          </div>

          <PrimaryButton onClick={handleSendSmsCode} disabled={smsBusy || captchaPending || !phoneNumber.replace(/\D/g, '')}
            style={{ backgroundColor: smsBusy || captchaPending || !phoneNumber.replace(/\D/g, '') ? '#D1D5DB' : 'var(--tenant-primary, #000)' }}>
            {smsBusy ? 'Sending…' : captchaPending ? 'Verifying…' : (effectiveCodeChannel(deliveryMethod, whatsappCode) === 'WHATSAPP' ? 'Send code via WhatsApp' : 'Send code via SMS')}
          </PrimaryButton>
        </Container>
      </Page>
    );
  }

  if (flowStep === 'pin') {
    return (
      <Page>
        <Container>
          <TopBar>
            <BackBtn onClick={() => { setSmsError(''); setFlowStep('phone'); }}>
              <ArrowLeft size={22} />
            </BackBtn>
            <TopBarTitle>Enter code</TopBarTitle>
            <div style={{ width: '34px' }} />
          </TopBar>

          {renderLogo()}

          <Title style={{ fontSize: '22px' }}>Enter the verification code</Title>
          <Subtitle style={{ textAlign: 'center' }}>
            We sent a 4-digit code to <strong>{fullPhone}</strong>
          </Subtitle>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', margin: '12px 0' }}>
            {pinDigits.map((d, i) => (
              <input
                key={i}
                id={`pin-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handlePinDigitChange(i, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(i, e)}
                autoFocus={i === 0}
                style={{
                  width: '52px',
                  height: '60px',
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: 600,
                  border: '1px solid #D1D5DB',
                  borderRadius: '10px',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            ))}
          </div>

          {smsError && (
            <div style={{ color: '#DC2626', fontSize: '13px', textAlign: 'center', marginTop: '4px' }}>
              {smsError}
            </div>
          )}

          {/* Re-mount the captcha so a fresh token is available for "Resend code". */}
          {resendTimer === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <TurnstileWidget key={`resend-${captchaKey}`} onToken={setCaptchaToken} action="send-sms-code" />
            </div>
          )}

          <button
            onClick={handleSendSmsCode}
            disabled={resendTimer > 0 || smsBusy || captchaPending}
            style={{
              background: 'none', border: 'none', padding: '12px',
              color: (resendTimer > 0 || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)) ? '#9CA3AF' : 'var(--tenant-primary, #000)',
              cursor: (resendTimer > 0 || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)) ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600,
            }}
          >
            {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
          </button>

          <PrimaryButton onClick={handleVerifyPin} disabled={smsBusy || pinDigits.join('').length < 4}
            style={{ backgroundColor: smsBusy || pinDigits.join('').length < 4 ? '#D1D5DB' : 'var(--tenant-primary, #000)' }}>
            {smsBusy ? 'Verifying…' : 'Verify'}
          </PrimaryButton>
        </Container>
      </Page>
    );
  }

  if (flowStep === 'guestForm') {
    const scanTitle = isMainGuest
      ? 'Scan your ID document'
      : `Scan ID for companion ${currentGuestIndex}`;
    return (
      <Page>
        <Container>
          <TopBar>
            <BackBtn onClick={handleGuestBack}>
              <ArrowLeft size={22} />
            </BackBtn>
            <TopBarTitle>{isMainGuest ? 'Main Guest' : `Companion ${currentGuestIndex}`}</TopBarTitle>
            <div style={{ width: '34px' }} />
          </TopBar>

          <GuestScanForm
            guest={{
              name: currentGuest.name,
              last_name: currentGuest.last_name,
              document_id: currentGuest.document_id,
              dob: currentGuest.dob,
              nationality: currentGuest.nationality,
              expiration_date: currentGuest.expiration_date,
            }}
            onChange={handleGuestFieldChange}
            scanning={scanning}
            scanStatus={scanStatus}
            scannedPreview={scannedPreview}
            onScanFile={handleScanDocument}
            scanTitle={scanTitle}
            error={formError}
            tenantPrimaryColor={tenantPrimaryColor}
            autoFocusFirstField
          />

          <PrimaryButton
            onClick={handleGuestContinue}
            disabled={!isFormValid}
            style={{ backgroundColor: !isFormValid ? '#D1D5DB' : 'var(--tenant-primary, #000)' }}
          >
            {isLastGuest ? 'Confirm invitation' : 'Save & continue'}
          </PrimaryButton>
        </Container>
      </Page>
    );
  }

  // submitting
  return (
    <Page>
      <Container>
        {renderLogo()}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '40px 0', color: '#6B7280' }}>
          <Spinner />
          <div style={{ fontSize: '15px' }}>Confirming your invitation…</div>
        </div>
      </Container>
    </Page>
  );
};
